import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Users, Award, Building2, Globe, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo, stats } from '../data/mock';

const About = () => {
  const milestones = [
    { year: '2010', title: 'Company Founded', description: 'Pinto Trading established in Newark' },
    { year: '2013', title: 'Global Expansion', description: 'Expanded operations to Asia and Europe' },
    { year: '2016', title: '500+ Partners', description: 'Reached milestone of 500 business partners' },
    { year: '2019', title: 'Quality Certified', description: 'Achieved ISO 9001 quality certification' },
    { year: '2022', title: '50 Countries', description: 'Now serving clients in over 50 countries' },
    { year: '2025', title: 'Industry Leader', description: 'Recognized as leading global trading company' },
  ];

  const values = [
    {
      icon: Target,
      title: 'Mission',
      description: 'To connect businesses globally with quality products and reliable trading services, fostering growth and success for our partners.'
    },
    {
      icon: Users,
      title: 'Vision',
      description: 'To be the most trusted and preferred global trading partner, known for excellence, integrity, and innovation.'
    },
    {
      icon: Award,
      title: 'Values',
      description: 'Integrity, Quality, Reliability, Customer Focus, and Continuous Improvement guide everything we do.'
    }
  ];

  return (
    <main>
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 via-slate-100/50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              About <span className="text-slate-700">Pinto Trading</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              {companyInfo.description} We pride ourselves on building lasting relationships with clients and suppliers around the world.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-slate-900 mb-2">{stat.value}</p>
                <p className="text-slate-600">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Our Foundation</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Built on strong principles that guide our every decision
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((item, index) => (
              <div key={index} className="bg-slate-50 rounded-2xl p-8 hover:bg-slate-100 transition-colors">
                <div className="w-14 h-14 bg-slate-900 rounded-xl flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">Our Story</h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Founded in 2010, Pinto Trading began with a simple vision: to make global trade accessible and reliable for businesses of all sizes.
                </p>
                <p>
                  What started as a small trading operation has grown into a comprehensive trading company with partnerships spanning over 50 countries. Our success is built on trust, quality, and a deep understanding of international markets.
                </p>
                <p>
                  Today, we continue to expand our reach while maintaining the personalized service and attention to detail that has defined us from day one.
                </p>
              </div>
              
              <div className="mt-8 flex flex-wrap gap-4">
                <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg border border-slate-200">
                  <Building2 className="w-5 h-5 text-slate-700" />
                  <span className="text-slate-700 font-medium">Est. {companyInfo.founded}</span>
                </div>
                <div className="flex items-center gap-3 bg-white px-4 py-3 rounded-lg border border-slate-200">
                  <Globe className="w-5 h-5 text-slate-700" />
                  <span className="text-slate-700 font-medium">50+ Countries</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-10 text-white">
              <h3 className="text-2xl font-bold mb-8">What Sets Us Apart</h3>
              <ul className="space-y-4">
                {[
                  'Direct relationships with manufacturers',
                  'Rigorous quality control processes',
                  'Flexible order quantities',
                  'Dedicated account management',
                  'Transparent pricing structure',
                  'On-time delivery guarantee'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-slate-300 flex-shrink-0" />
                    <span className="text-slate-200">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Our Journey</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Key milestones in our growth story
            </p>
          </div>

          <div className="relative">
            {/* Timeline line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-slate-200" />
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className={`flex flex-col md:flex-row items-center gap-8 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <div className={`bg-slate-50 rounded-xl p-6 inline-block ${index % 2 === 0 ? 'md:ml-auto' : 'md:mr-auto'}`}>
                      <span className="text-slate-700 font-bold text-lg">{milestone.year}</span>
                      <h3 className="text-slate-900 font-semibold mt-1">{milestone.title}</h3>
                      <p className="text-slate-600 text-sm mt-2">{milestone.description}</p>
                    </div>
                  </div>
                  <div className="w-4 h-4 bg-slate-900 rounded-full border-4 border-slate-200 z-10" />
                  <div className="flex-1 hidden md:block" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Work With Us?
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Join our network of satisfied partners and experience the Pinto Trading difference.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-slate-100 hover:bg-white text-slate-900 px-8">
              Contact Our Team
            </Button>
          </Link>
        </div>
      </section>
    </main>
  );
};

export default About;
